import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { customerService } from "../services/customer.service";

// Async thunks
export const fetchCustomers = createAsyncThunk(
  "customer/fetchCustomers",
  async (_, { rejectWithValue }) => {
    try {
      return await customerService.getCustomers();
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération des clients",
      );
    }
  },
);

export const fetchActiveCustomers = createAsyncThunk(
  "customer/fetchActiveCustomers",
  async (_, { rejectWithValue }) => {
    try {
      return await customerService.getActiveCustomers();
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération des clients actifs",
      );
    }
  },
);

export const fetchCustomerById = createAsyncThunk(
  "customer/fetchCustomerById",
  async (id, { rejectWithValue }) => {
    try {
      return await customerService.getCustomerById(id);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération du client",
      );
    }
  },
);

export const searchCustomersByName = createAsyncThunk(
  "customer/searchCustomersByName",
  async (name, { rejectWithValue }) => {
    try {
      return await customerService.getCustomerByName(name);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la recherche de clients",
      );
    }
  },
);

export const addCustomer = createAsyncThunk(
  "customer/addCustomer",
  async (customer, { rejectWithValue }) => {
    try {
      const result = await customerService.createCustomer(customer);
      return result;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Erreur lors de la création du client",
      );
    }
  },
);

export const editCustomer = createAsyncThunk(
  "customer/editCustomer",
  async ({ id, customer }, { rejectWithValue }) => {
    try {
      await customerService.updateCustomer(id, customer);
      return { id, ...customer };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la mise à jour du client",
      );
    }
  },
);

export const removeCustomer = createAsyncThunk(
  "customer/removeCustomer",
  async (id, { rejectWithValue }) => {
    try {
      await customerService.deleteCustomer(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la suppression du client",
      );
    }
  },
);
export const fetchBeneficiaryCustomers = createAsyncThunk(
  "customer/fetchBeneficiaryCustomers",
  async (_, { rejectWithValue }) => {
    try {
      return await customerService.getBeneficiaryCustomers();
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération des clients bénéficiaires",
      );
    }
  },
);

export const fetchCustomerSummaries = createAsyncThunk(
  "customer/fetchCustomerSummaries",
  async (isBeneficiary = false, { rejectWithValue }) => {
    try {
      return await customerService.getCustomerSummaries(isBeneficiary);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Erreur lors de la récupération des résumés des clients",
      );
    }
  }
);

export const importCustomers = createAsyncThunk(
  "customer/importCustomers",
  async (file, { rejectWithValue }) => {
    try {
      return await customerService.importCustomers(file);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Erreur lors de l'import des clients",
      );
    }
  },
);

// Initial state
const initialState = {
  customers: [],
  totalCount: 0,
  searchResults: [],
  selectedCustomer: null,
  loading: false,
  searchLoading: false,
  error: null,
  showModal: false,
  showDeleteModal: false,
  searchQuery: "",
};

// Slice
const customerSlice = createSlice({
  name: "customer",
  initialState,
  reducers: {
    openModal: (state) => {
      state.showModal = true;
    },
    closeModal: (state) => {
      state.showModal = false;
    },
    openDeleteModal: (state) => {
      state.showDeleteModal = true;
    },
    closeDeleteModal: (state) => {
      state.showDeleteModal = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSelectedCustomer: (state, action) => {
      state.selectedCustomer = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchQuery = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch customers
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = action.payload;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch active customers
      .addCase(fetchActiveCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = action.payload;
      })
      .addCase(fetchActiveCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Search customers by name
      .addCase(searchCustomersByName.pending, (state) => {
        state.searchLoading = true;
        state.error = null;
      })
      .addCase(searchCustomersByName.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload;
      })
      .addCase(searchCustomersByName.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.payload;
      })

      // Add customer
      .addCase(addCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.customers.push(action.payload);
        state.showModal = false;
      })
      .addCase(addCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Edit customer
      .addCase(editCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editCustomer.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.customers.findIndex(
          (customer) => customer.customerId === action.payload.id,
        );
        if (index !== -1) {
          state.customers[index] = action.payload;
        }
        state.showModal = false;
      })
      .addCase(editCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Remove customer
      .addCase(removeCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = state.customers.filter(
          (customer) => customer.customerId !== action.payload,
        );
        state.showDeleteModal = false;
      })
      .addCase(removeCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchBeneficiaryCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBeneficiaryCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = action.payload;
      })
      .addCase(fetchBeneficiaryCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCustomerSummaries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerSummaries.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = action.payload; // Updates the dropdown data with the lightweight DTO
      })
      .addCase(fetchCustomerSummaries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  openModal,
  closeModal,
  openDeleteModal,
  closeDeleteModal,
  clearError,
  setSelectedCustomer,
  setSearchQuery,
  clearSearchResults,
} = customerSlice.actions;

export default customerSlice.reducer;
